import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, KeyRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth, formatApiError } from "../../lib/auth";
import { authChangePassword } from "../../lib/api";

export function AccountBar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const doLogout = async () => {
    await logout();
    toast.success("Logged out");
    navigate("/");
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setError("");
    if (form.next.length < 8) { setError("New password must be at least 8 characters"); return; }
    if (form.next !== form.confirm) { setError("New passwords do not match"); return; }
    setBusy(true);
    try {
      await authChangePassword(form.current, form.next);
      toast.success("Password changed — use it next time you log in");
      setDialogOpen(false);
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full px-3 py-2 rounded bg-deep border border-white/10 text-white text-xs font-mono focus:outline-none focus:border-cyber";

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="admin-account-bar">
      <span className="font-mono text-[11px] text-dim hidden sm:inline" data-testid="admin-logged-in-email">{user?.email}</span>
      <button type="button" onClick={() => setDialogOpen(true)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-white/20 text-dim font-mono text-[11px] hover:text-cyber hover:border-cyber transition-colors"
        data-testid="admin-change-password-button">
        <KeyRound className="w-3 h-3" /> Change Password
      </button>
      <button type="button" onClick={doLogout}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-white/20 text-dim font-mono text-[11px] hover:text-red-400 hover:border-red-400 transition-colors"
        data-testid="admin-logout-button">
        <LogOut className="w-3 h-3" /> Logout
      </button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-panel border-white/10 text-mist max-w-sm" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-white uppercase">Change Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={changePassword} className="space-y-4">
            {error && (
              <div className="p-3 rounded bg-red-500/10 border border-red-500/40 font-mono text-xs text-red-400" data-testid="change-password-error">
                {error}
              </div>
            )}
            <div>
              <label className="block font-mono text-xs text-dim mb-1" htmlFor="cp-current">Current password</label>
              <input id="cp-current" type="password" required autoComplete="current-password" className={inputCls}
                value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })}
                data-testid="change-password-current" />
            </div>
            <div>
              <label className="block font-mono text-xs text-dim mb-1" htmlFor="cp-next">New password (min 8 characters)</label>
              <input id="cp-next" type="password" required minLength={8} autoComplete="new-password" className={inputCls}
                value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })}
                data-testid="change-password-new" />
            </div>
            <div>
              <label className="block font-mono text-xs text-dim mb-1" htmlFor="cp-confirm">Repeat new password</label>
              <input id="cp-confirm" type="password" required minLength={8} autoComplete="new-password" className={inputCls}
                value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                data-testid="change-password-confirm" />
            </div>
            <button type="submit" disabled={busy}
              className="w-full px-5 py-2.5 rounded bg-cyber text-[#0b0f19] font-bold font-mono text-xs hover:bg-white transition-colors disabled:opacity-50"
              data-testid="change-password-save">
              {busy ? "Saving..." : "Save New Password"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
